<template>
  <div class="style-rules-manager">
    <!-- System Status Alert -->
    <v-alert
      v-if="systemStatus.type !== 'success'"
      :type="systemStatus.type"
      class="mb-4"
      dismissible
    >
      {{ systemStatus.message }}
    </v-alert>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate size="64" />
      <p class="mt-4 text-grey">Loading style rules...</p>
    </div>

    <!-- Main Content -->
    <div v-else>
      <div class="d-flex justify-space-between align-center mb-4">
        <div class="d-flex align-center" style="gap: 1.5rem;">
          <h3>Style Guide Rules ({{ rules.length }} rules)</h3>
        </div>
        <div class="d-flex gap-2 align-center">
          <!-- Data Actions -->
          <v-btn
            color="info"
            @click="$emit('export-rules')"
            :disabled="rules.length === 0"
          >
            <v-icon left>mdi-download</v-icon>
            Export CSV
          </v-btn>

          <!-- Import Actions -->
          <v-btn
            color="orange"
            @click="$refs.csvFileInput.click()"
            variant="outlined"
          >
            <v-icon left>mdi-upload</v-icon>
            Import CSV
          </v-btn>
          <input
            ref="csvFileInput"
            type="file"
            accept=".csv"
            style="display: none"
            @change="onFileImport"
          />

          <!-- Create Actions -->
          <v-divider vertical class="mx-2"></v-divider>
          <v-btn color="primary" @click="addNewRule">
            <v-icon left>mdi-plus</v-icon>
            Add New Rule
          </v-btn>
        </div>
      </div>

      <!-- Empty State for Rules -->
      <v-card v-if="rules.length === 0" class="text-center py-8">
        <v-card-text>
          <v-icon size="64" color="grey-lighten-1"
            >mdi-book-open-variant</v-icon
          >
          <h3 class="mt-4 mb-2">No Style Rules Yet</h3>
          <p class="text-grey mb-4">Create your first style guide rule</p>
          <v-btn color="primary" @click="addNewRule">
            <v-icon left>mdi-plus</v-icon>
            Create First Rule
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Rules Table -->
      <v-card v-else>
        <v-data-table
          :headers="ruleHeaders"
          :items="rules"
          :items-per-page="10"
          class="elevation-1"
        >
          <template v-slot:item.brands="{ item }">
            <div v-if="isAllBrands(item.brands)">
              <v-chip size="small" color="primary" variant="outlined">
                All Brands
              </v-chip>
            </div>
            <div v-else class="d-flex flex-wrap" style="gap: 4px;">
              <v-tooltip
                v-for="(brandId, idx) in getBrandArray(item.brands).slice(0, 2)"
                :key="idx"
                :text="getBrandTooltip(brandId)"
                location="top"
              >
                <template v-slot:activator="{ props }">
                  <v-chip size="small" color="primary" variant="outlined" v-bind="props">
                    {{ getBrandDisplayName(brandId) }}
                  </v-chip>
                </template>
              </v-tooltip>
              <v-tooltip
                v-if="getBrandArray(item.brands).length > 2"
                location="top"
              >
                <template v-slot:activator="{ props }">
                  <v-chip size="small" color="primary" variant="text" v-bind="props">
                    +{{ getBrandArray(item.brands).length - 2 }}
                  </v-chip>
                </template>
                <div>
                  <div v-for="(brandId, idx) in getBrandArray(item.brands).slice(2)" :key="idx">
                    {{ getBrandDisplayName(brandId) }} ({{ brandId }})
                  </div>
                </div>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.areas="{ item }">
            <div v-if="item.areas">
              <v-chip
                v-for="(field, key) in item.areas"
                :key="key"
                v-show="field === true"
                size="x-small"
                color="purple"
                variant="outlined"
                class="ma-1"
              >
                <!-- convert to title case -->
                {{ key.replace("_", " ").replace(/\b\w/g, s => s.toUpperCase()) }}
              </v-chip>
              <span v-if="!Object.values(item.areas).some(v => v)" class="text-caption text-grey">—</span>
            </div>
            <span v-else class="text-caption text-grey">—</span>
          </template>

          <template v-slot:item.bricks="{ item }">
            <div v-if="item.bricks && item.bricks.length > 0">
              <v-chip
                v-for="(gpc, idx) in item.bricks.slice(0, 2)"
                :key="idx"
                size="x-small"
                class="mr-1 mb-1"
                color="blue-grey"
                variant="outlined"
              >
                {{ gpcList.find(o => o.code === gpc).display }}
              </v-chip>
              <v-chip
                v-if="item.bricks.length > 2"
                size="x-small"
                color="blue-grey"
                variant="text"
              >
                +{{ item.bricks.length - 2 }}
              </v-chip>
            </div>
            <span v-else class="text-caption text-grey">—</span>
          </template>

          <template v-slot:item.segments="{ item }">
            <div v-if="item.segments && item.segments.length > 0">
              <v-chip
                v-for="(gpc, idx) in item.segments.slice(0, 2)"
                :key="idx"
                size="x-small"
                class="mr-1 mb-1"
                color="blue-grey"
                variant="outlined"
              >
                {{ segmentItems.find(o => o.value === gpc).title }}
              </v-chip>
              <v-chip
                v-if="item.segments.length > 2"
                size="x-small"
                color="blue-grey"
                variant="text"
              >
                +{{ item.segments.length - 2 }}
              </v-chip>
            </div>
            <span v-else class="text-caption text-grey">—</span>
          </template>

          <template v-slot:item.rule_type="{ item }">
            <v-chip
              size="small"
              color="info"
              variant="flat"
            >
              {{ ruleTypes.find((type) => type.value === item.rule_type).title }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon size="small" @click="editRule(item)" class="mr-1">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon size="small" color="error" @click="deleteRule(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Edit/Create Rule Dialog -->
    <v-dialog v-model="showRuleDialog" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center" style="gap: 12px;">
          <v-icon color="white">mdi-book-edit</v-icon>
          <span>
            {{
              editingRule.rule_index !== undefined
                ? "Edit Style Rule"
                : "Create New Style Rule"
            }}
          </span>
        </v-card-title>

        <v-card-text style="max-height: 70vh" class="pt-6">
          <v-form ref="ruleForm">
            <!-- Basic Information Section -->
            <div class="mb-5">
              <div class="text-overline text-primary mb-3">Basic Information</div>
              
              <!-- Rule Name -->
              <v-text-field
                v-model="editingRule.rule_name"
                label="Rule Name"
                :rules="[(v) => !!v || 'Rule name is required']"
                required
                variant="outlined"
                density="comfortable"
                class="mb-4"
              />

              <!-- Description -->
              <v-textarea
                v-model="editingRule.rule_description"
                label="Description"
                placeholder="Brief description of this style rule"
                rows="2"
                auto-grow
                variant="outlined"
                density="comfortable"
                class="mb-0"
              />
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Applicability Section -->
            <div class="mb-5">
              <div class="text-overline text-primary mb-3">Applicability</div>

              <v-autocomplete chips closable-chips multiple variant="outlined" label="Attributes"
                  v-model="editingRule.attribute_ids" :items="attributesSimplified" >
              </v-autocomplete>

              <!-- Applicable GPC -->
              <v-autocomplete chips closable-chips multiple variant="outlined" label="GPC Bricks"
                v-model="editingRule.bricks" :items="gpcList" item-title="display" item-value="code" >
              </v-autocomplete>

              <!-- Segment Brick -->
              <v-autocomplete chips closable-chips multiple variant="outlined" label="GPC Segments"
                v-model="editingRule.segments" :items="segmentItems"
              />

              <!-- Applicable Brands -->
              <div class="mb-4">
                <v-autocomplete
                  v-model="editingRule.brands"
                  :items="brandOptions"
                  :loading="brandsLoading"
                  item-title="title"
                  item-value="value"
                  label="Applicable Brands"
                  placeholder="Select 'All Brands' or search for specific brands..."
                  no-data-text="Type to search brands"
                  multiple
                  chips
                  closable-chips
                  clearable
                  no-filter
                  variant="outlined"
                  density="comfortable"
                  :rules="[(v) => (v && v.length > 0) || 'Please select applicable brands']"
                  @update:search="onBrandSearch"
                >
                <template v-slot:chip="{ item, props }">
                  <v-chip
                    v-bind="props"
                    :text="getBrandChipText(item.value)"
                  />
                </template>
                <template v-slot:append-inner>
                  <v-tooltip location="top" max-width="350">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="grey">mdi-help-circle</v-icon>
                    </template>
                    <div>
                      <strong>Search 50,000+ brands by:</strong><br>
                      • Brand name (e.g., "Disney", "Nike")<br>
                      • Brand ID (e.g., "90015278")<br>
                      <em class="text-caption">Start typing to search the database</em>
                    </div>
                  </v-tooltip>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title>
                      Type to search 50,000+ brands by name or ID
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <div class="text-caption text-grey mt-1 d-flex align-center" style="gap: 4px;">
                <v-icon size="16" color="grey-lighten-1">mdi-information</v-icon>
                <span>Search by brand name (e.g., "Disney") or ID (e.g., "90015278")</span>
              </div>
            </div>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Impacted Fields Section -->
            <div class="mb-5">
              <div class="text-overline text-primary mb-3">Impacted Fields</div>
              <v-card variant="outlined" class="pa-4">
                <div class="d-flex flex-wrap" style="gap: 16px;">
                  <v-checkbox
                    v-model="editingRule.areas.title"
                    label="Title"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                  <v-checkbox
                    v-model="editingRule.areas.description"
                    label="Description"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                  <v-checkbox
                    v-model="editingRule.areas.bullet_points"
                    label="Bullet Points"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                  <v-checkbox
                    v-model="editingRule.areas.color"
                    label="Color"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                  <v-checkbox
                    v-model="editingRule.areas.size"
                    label="Size"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                  <v-checkbox
                    v-model="editingRule.areas.product_attributes"
                    label="Product Attributes"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                  <v-checkbox
                    v-model="editingRule.areas.variant_attributes"
                    label="Variant Attributes"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                  <v-checkbox
                    v-model="editingRule.areas.frontend_categories"
                    label="Frontend Categories"
                    density="compact"
                    hide-details
                    color="primary"
                  />
                </div>
              </v-card>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Rule Configuration Section -->
            <div class="mb-5">
              <div class="text-overline text-primary mb-3">Rule Configuration</div>
              
              <!-- Rule Type -->
              <v-select
                v-model="editingRule.rule_type"
                :items="ruleTypes"
                label="Rule Type"
                :rules="[(v) => !!v || 'Rule type is required']"
                required
                variant="outlined"
                density="comfortable"
                class="mb-4"
              />

              <!-- Instructions (Rich Text) -->
              <div>
                <label class="text-subtitle-2 mb-2 d-block font-weight-medium"
                  >Instructions (Rich Text Supported)</label
                >
              <div class="instruction-toolbar mb-2">
                <v-btn-group density="compact" variant="outlined" size="small">
                  <v-btn @click="insertFormatting('**', '**')" title="Bold">
                    <v-icon>mdi-format-bold</v-icon>
                  </v-btn>
                  <v-btn @click="insertFormatting('*', '*')" title="Italic">
                    <v-icon>mdi-format-italic</v-icon>
                  </v-btn>
                  <v-btn
                    @click="insertFormatting('\n- ', '')"
                    title="Bullet List"
                  >
                    <v-icon>mdi-format-list-bulleted</v-icon>
                  </v-btn>
                  <v-btn
                    @click="insertFormatting('\n1. ', '')"
                    title="Numbered List"
                  >
                    <v-icon>mdi-format-list-numbered</v-icon>
                  </v-btn>
                </v-btn-group>
              </div>
              <v-textarea
                ref="instructionField"
                v-model="editingRule.instruction"
                placeholder="Enter detailed instructions. Use **bold** for emphasis, *italic* for notes, and lists for steps."
                rows="8"
                variant="outlined"
                density="comfortable"
                auto-grow
                outlined
                hint="Supports markdown-style formatting: **bold**, *italic*, bullet lists, etc."
                persistent-hint
              />
            </div>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn 
            variant="text" 
            size="large"
            @click="closeRuleDialog"
            :disabled="savingRule"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            size="large"
            variant="elevated"
            :disabled="!isRuleValid"
            :loading="savingRule"
            @click="saveRule"
          >
            <v-icon left>{{ editingRule.rule_index !== undefined ? "mdi-content-save" : "mdi-plus" }}</v-icon>
            {{ editingRule.rule_index !== undefined ? "Update" : "Create" }} Rule
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { createNewStyleGuideRule, deleteStyleGuideRule, editExistingStyleGuideRule } from "./StyleRulesManager.api";

// {
//   "brands": brands_raw,
// }

const props = defineProps({
  rules: {
    type: Array,
    default: () => [],
  },
  rulesId: {
    type: String,
    default: "",
  },
  gpcList: {
    type: Array,
    default: () => [],
  },
  segmentBricks: {
    type: Array,
    default: () => [],
  },
  attributeData: Object,
  loading: {
    type: Boolean,
    default: false,
  },
  systemStatus: {
    type: Object,
    default: () => ({ type: "success", message: "" }),
  },
  currentVersion: String,
  hasUnsavedChanges: Boolean,
  lastPublishInfo: Object,
  brandsList: {
    type: Array,
    default: () => [],
  },
  brandsLoading: {
    type: Boolean,
    default: false,
  },
  currentEnvironment: {
    type: String,
    default: 'test',
  },
  taxonomyFile: {
    type: String,
    default: 'simplified_taxonomy_deduplicated',
  },
});

const emit = defineEmits([
  "rule-changed",
  "export-rules",
  "import-rules",
  "environment-changed",
  "rule-updated",
  "rule-created",
  "search-brands",
]);

// Local state
const showRuleDialog = ref(false);
const selectedEnvironment = ref(props.currentEnvironment);
const ruleForm = ref(null);
const csvFileInput = ref(null);
const instructionField = ref(null);
const savingRule = ref(false);

// Brand cache for displaying brand names in the table
const brandCache = ref({});
const BRANDS_API_URL = 'https://brands-mini-api-844472556697.europe-west6.run.app';

// Environment options
const environmentOptions = [
  { title: 'Test', value: 'test', color: 'orange', icon: 'mdi-test-tube' },
  { title: 'Production', value: 'production', color: 'green', icon: 'mdi-check-circle' },
];

const attributesSimplified = computed(() => {
    return Object.entries(props.attributeData).map(([id, rest]) => {
        return { value: id, title: `${rest.display_name.en} (${rest.name})` };
    });
});

const editingRule = ref({
  id: null,
  rule_name: "",
  brands: ["All"],
  segments: [],
  bricks: [],
  areas: {
    title: false,
    description: false,
    bullet_points: false,
    color: false,
    size: false,
    product_attributes: false,
    variant_attributes: false,
    frontend_categories: false,
  },
  rule_type: "llm model",
  instruction: "",
});

// Static options
// Brand options - 'All' plus dynamic brands from API
const brandOptions = computed(() => {
  // Filter out any brands with 'All' as their ID to avoid duplicates
  const brands = props.brandsList
    .filter((b) => {
      const id = b.brandId || b.name;
      return id && id !== 'All';
    })
    .map((b) => ({
      title: `${b.name || "Unknown"} (${b.brandId || "unknown"})`,
      value: `${b.brandId}` || `${b.name}` || "unknown",
    }))
    .sort((a, b) => a.title.localeCompare(b.title)); // Sort alphabetically by brand name
  
  const options = [{ title: "All Brands", value: "All" }, ...brands];
  console.log(
    `🏷️ Brand options available: ${options.length} (${brands.length} brands + All)`
  );
  return options;
});

const ruleTypes = [
  { title: "Change Exclusion", value: "change exclusion" },
  { title: "Creation Exclusion", value: "creation exclusion" },
  { title: "Size Formatting", value: "size formatting" },
  { title: "LLM Model", value: "llm model" },
];

// Table headers
const ruleHeaders = [
  {
    title: "Rule Name",
    key: "rule_name",
    sortable: true,
  },
  { title: "Brands", key: "brands", sortable: true },
  { title: "GPC Segments", key: "segments", sortable: true },
  { title: "GPC Bricks", key: "bricks", sortable: false },
  { title: "Impacted Fields", key: "areas", sortable: false },
  { title: "Rule Type", key: "rule_type", sortable: true },
  { title: "Actions", key: "actions", sortable: false, align: "end" },
];

// Fetch brand data by ID and cache it
const fetchBrandById = async (brandId) => {
  if (!brandId || brandId === 'All' || brandCache.value[brandId]) {
    return;
  }

  try {
    const url = new URL(BRANDS_API_URL);
    url.searchParams.set('brandId', brandId);
    
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`Failed to fetch brand ${brandId}`);
      return;
    }
    
    const result = await response.json();
    
    // Handle different response formats
    let brandData = null;
    if (result.data && Array.isArray(result.data) && result.data.length > 0) {
      brandData = result.data[0];
    } else if (Array.isArray(result) && result.length > 0) {
      brandData = result[0];
    } else if (result.brandId && result.name) {
      brandData = result;
    }
    
    if (brandData) {
      brandCache.value[brandId] = {
        brandId: `${brandData.brandId}`,
        name: brandData.name
      };
    }
  } catch (error) {
    console.warn(`Error fetching brand ${brandId}:`, error);
  }
};

// Helper functions for brand display
const getBrandArray = (brandValue) => {
  if (!brandValue) return [];
  if (Array.isArray(brandValue)) return brandValue;
  return [brandValue];
};

const isAllBrands = (brandValue) => {
  const brands = getBrandArray(brandValue);
  return brands.length === 0 || brands.includes("All") || brands[0] === "All";
};

const getBrandChipText = (brandValue) => {
  if (!brandValue || brandValue === "All") {
    return "All Brands";
  }
  
  // First check brandsList (from parent - used in autocomplete)
  const brandInList = props.brandsList.find(b => String(b.brandId) === String(brandValue));
  if (brandInList) {
    return `${brandInList.name} (${brandInList.brandId})`;
  }
  
  // Then check local cache
  const brandInCache = brandCache.value[String(brandValue)];
  if (brandInCache) {
    return `${brandInCache.name} (${brandInCache.brandId})`;
  }
  
  // If not found, fetch it and return the ID for now
  fetchBrandById(brandValue);
  return brandValue;
};

const getBrandDisplayName = (brandValue) => {
  if (!brandValue || brandValue === "All") {
    return "All Brands";
  }
  
  // First check brandsList (from parent - used in autocomplete)
  const brandInList = props.brandsList.find(b => String(b.brandId) === String(brandValue));
  if (brandInList) {
    return brandInList.name;
  }
  
  // Then check local cache
  const brandInCache = brandCache.value[String(brandValue)];
  if (brandInCache) {
    return brandInCache.name;
  }
  
  // If not found, fetch it and return the ID for now
  fetchBrandById(brandValue);
  return brandValue;
};

const getBrandTooltip = (brandValue) => {
  if (!brandValue || brandValue === "All") {
    return "Applies to all brands";
  }
  
  return `Brand ID: ${brandValue}`;
};

// Computed
const isRuleValid = computed(() => {
  const brands = getBrandArray(editingRule.value.brands);
  return (
    (editingRule.value.rule_name) &&
    brands.length > 0 &&
    editingRule.value.rule_type
  );
});

// Methods
const addNewRule = () => {
  editingRule.value = {
    id: null,
    rule_name: "",
    brands: ["All"],
    segments: [],
    bricks: [],
    areas: {
      title: false,
      description: false,
      bullet_points: false,
      color: false,
      size: false,
      product_attributes: false,
      variant_attributes: false,
      frontend_categories: false,
    },
    rule_type: "llm model",
    instruction: "",
  };
  showRuleDialog.value = true;
};

const editRule = (rule) => {
  console.log("✏️ Editing rule:", rule);
  console.log("📦 Available gpcList:", props.gpcList);
  console.log("📝 Rule's bricks:", rule.bricks);
  
  // Normalize brands to always be an array
  let brands = getBrandArray(rule.brands);
  if (brands.length === 0) {
    brands = ["All"];
  }
  
  // Fetch brand data for selected brands if not in cache
  brands.forEach(brandId => {
    if (brandId && brandId !== 'All') {
      fetchBrandById(brandId);
    }
  });
  
  editingRule.value = { 
    ...rule,
    brands,
  };
  console.log("🎯 editingRule.bricks set to:", editingRule.value.bricks);
  console.log("🏷️ editingRule.brands set to:", editingRule.value.brands);
  showRuleDialog.value = true;
};

const saveRule = async () => {
  if (!isRuleValid.value) return;

  savingRule.value = true;
  try {
    console.log("💾 Saving rule:", editingRule.value);
    const dbname = selectedEnvironment.value;
    const taxonomyFile = props.taxonomyFile;

    // Transform component format to MongoDB format
    const mongoRule = transformComponentToMongo(editingRule.value);
    const rule_index = editingRule.value.rule_index;
    
    // Add taxonomy_file to the rule for filtering
    mongoRule.taxonomy_file = taxonomyFile;

    // We're editing an existing rule on a pre-existing style guide (DOER)
    if (rule_index !== undefined) {
      console.log(`🔄 Updating rule in ${dbname} for taxonomy: ${taxonomyFile}`);
      await editExistingStyleGuideRule(taxonomyFile, props.rulesId, rule_index, mongoRule, dbname);
      console.log('✅ Rule updated successfully');
      emit('rule-updated', { environment: dbname });
    }

    // We're adding a new rule on a pre-existing style guide (DOER)
    if (rule_index === undefined) {
      console.log(`➕ Creating new rule in ${dbname} for taxonomy: ${taxonomyFile}`);
      const result = await createNewStyleGuideRule(dbname, taxonomyFile, props.rulesId, mongoRule);
      console.log('✅ Rule created successfully:', result);
      emit('rule-created', { environment: dbname });
    }

    closeRuleDialog();
  } catch (error) {
    console.error('❌ Error saving rule:', error);
    alert(`Failed to save rule: ${error.message}`);
  } finally {
    savingRule.value = false;
  }
};

const deleteRule = async (rule) => {
  if (confirm(`Are you sure you want to delete the rule "${ rule.rule_name }"?`)) {
    const dbname = selectedEnvironment.value;
    const taxonomyFile = props.taxonomyFile;
    const rule_index = editingRule.value.rule_index;

    console.log(`🗑️ Deleting rule in ${dbname} for taxonomy: ${taxonomyFile}`);
    const result = await deleteStyleGuideRule(dbname, taxonomyFile, props.rulesId, rule.rule_index);
    console.log('✅ Rule deleted successfully:', result);
    // Emit delete event to parent to handle API call
    emit("delete-rule", { environment: dbname });
  }
};

const closeRuleDialog = () => {
  showRuleDialog.value = false;
  editingRule.value = {
    id: null,
    rule_name: "",
    brands: ["All"],
    segments: [],
    bricks: [],
    areas: {
      title: false,
      description: false,
      bullet_points: false,
      color: false,
      size: false,
      product_attributes: false,
      variant_attributes: false,
      frontend_categories: false,
    },
    rule_type: "llm model",
    instruction: "",
  };
};

// Transform component format to MongoDB format
const transformComponentToMongo = (componentRule) => {
  const brands = getBrandArray(componentRule.brands);
  const isAll = isAllBrands(componentRule.brands);
  
  return {
    brands: isAll ? [] : brands.filter(b => b !== 'All'),
    
    // New stuff down here
    segments: componentRule.segments || [],
    bricks: componentRule.bricks || [],
    attribute_ids: componentRule.attribute_ids || [],
    areas: componentRule.areas,
    rule_name: componentRule.rule_name,
    rule_description: componentRule.rule_description,
    instruction: componentRule.instruction,
    rule_type: componentRule.rule_type,
  };
};

// Handle brand search
const onBrandSearch = (searchQuery) => {
  console.log('🔍 Brand search triggered:', searchQuery);
  emit('search-brands', searchQuery);
};

// Handle environment change
const onEnvironmentChange = (newEnvironment) => {
  console.log(`🔄 Environment changed to: ${newEnvironment}`);
  emit('environment-changed', newEnvironment);
};

const onFileImport = (event) => {
  const file = event.target.files[0];
  if (file) {
    emit("import-rules", file);
  }
};

// Rich text formatting helper
const insertFormatting = (before, after) => {
  const textarea = instructionField.value?.$el?.querySelector("textarea");
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = editingRule.value.instruction || "";
  const selectedText = text.substring(start, end);

  if (selectedText) {
    // Wrap selected text
    editingRule.value.instruction =
      text.substring(0, start) +
      before +
      selectedText +
      after +
      text.substring(end);
  } else {
    // Insert at cursor
    editingRule.value.instruction =
      text.substring(0, start) + before + after + text.substring(end);
  }

  // Focus back to textarea
  setTimeout(() => {
    textarea.focus();
    const newPos =
      start +
      before.length +
      (selectedText ? selectedText.length + after.length : 0);
    textarea.setSelectionRange(newPos, newPos);
  }, 0);
};

// Load brand data for all rules when component mounts or rules change
const loadBrandsForRules = () => {
  const brandIds = new Set();
  
  props.rules.forEach(rule => {
    const brands = getBrandArray(rule.brands);
    brands.forEach(brandId => {
      if (brandId && brandId !== 'All' && !brandCache.value[brandId]) {
        brandIds.add(brandId);
      }
    });
  });
  
  brandIds.forEach(brandId => {
    fetchBrandById(brandId);
  });
};

const segmentItems = [
  {
    "title": "Pet Care/Food",
    "value": "10000000"
  },
  {
    "title": "Cleaning/Hygiene Products",
    "value": "47000000"
  },
  {
    "title": "Food/Beverage/Tobacco",
    "value": "50000000"
  },
  {
    "title": "Healthcare",
    "value": "51000000"
  },
  {
    "title": "Beauty/Personal Care/Hygiene",
    "value": "53000000"
  },
  {
    "title": "Cross Segment",
    "value": "58000000"
  },
  {
    "title": "Textual/Printed/Reference Materials",
    "value": "60000000"
  },
  {
    "title": "Music",
    "value": "61000000"
  },
  {
    "title": "Stationery/Office Machinery/Occasion Supplies",
    "value": "62000000"
  },
  {
    "title": "Footwear",
    "value": "63000000"
  },
  {
    "title": "Personal Accessories",
    "value": "64000000"
  },
  {
    "title": "Computing",
    "value": "65000000"
  },
  {
    "title": "Communications",
    "value": "66000000"
  },
  {
    "title": "Clothing",
    "value": "67000000"
  },
  {
    "title": "Audio Visual/Photography",
    "value": "68000000"
  },
  {
    "title": "Arts/Crafts/Needlework",
    "value": "70000000"
  },
  {
    "title": "Sports Equipment",
    "value": "71000000"
  },
  {
    "title": "Home Appliances",
    "value": "72000000"
  },
  {
    "title": "Kitchenware and Tableware",
    "value": "73000000"
  },
  {
    "title": "Camping",
    "value": "74000000"
  },
  {
    "title": "Household/Office Furniture/Furnishings",
    "value": "75000000"
  },
  {
    "title": "Vehicle",
    "value": "77000000"
  },
  {
    "title": "Electrical Supplies",
    "value": "78000000"
  },
  {
    "title": "Plumbing/Heating/Ventilation/Air Conditioning",
    "value": "79000000"
  },
  {
    "title": "Tools/Equipment - Hand",
    "value": "80000000"
  },
  {
    "title": "Lawn/Garden Supplies",
    "value": "81000000"
  },
  {
    "title": "Tools/Equipment - Power",
    "value": "82000000"
  },
  {
    "title": "Building Products",
    "value": "83000000"
  },
  {
    "title": "Tool Storage/Workshop Aids",
    "value": "84000000"
  },
  {
    "title": "Safety/Protection - DIY",
    "value": "85000000"
  },
  {
    "title": "Toys/Games",
    "value": "86000000"
  },
  {
    "title": "Fuels/Gases",
    "value": "87000000"
  },
  {
    "title": "Lubricants",
    "value": "88000000"
  },
  {
    "title": "Live Animals",
    "value": "89000000"
  },
  {
    "title": "Safety/Security/Surveillance",
    "value": "91000000"
  },
  {
    "title": "Storage/Haulage Containers",
    "value": "92000000"
  },
  {
    "title": "Horticulture Plants",
    "value": "93000000"
  },
  {
    "title": "Crops",
    "value": "94000000"
  },
  {
    "title": "Services/Vending Machines",
    "value": "95000000"
  },
  {
    "title": "Monetary Assets",
    "value": "96000000"
  }
];

// Watch for rules changes
watch(() => props.rules, () => {
  loadBrandsForRules();
}, { immediate: true, deep: true });

// Load brands on mount
onMounted(() => {
  loadBrandsForRules();
});
</script>

<style scoped>
.style-rules-manager {
  padding: 1rem;
}

.gap-2 {
  gap: 0.5rem;
}

.instruction-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.instruction-toolbar .v-btn-group {
  background: white;
}
</style>
